import { RssItem } from "../../types/rssTypes";
import { captureSentryException } from "../sentry/captureException";

const CUSTOM_FIELD_TAGS = [
  "paisDeOrigem",
  "cidadeDeOrigem",
  "cidadeDeDestino",
  "aeroportoDeOrigem",
  "aeroportoDeDestino",
  "paisDeDestino",
  "valorDaOferta",
  "valorMedio",
  "escala",
  "tempoEstimado",
  "mesesDeViagem",
  "descricaoDoAchado",
  "linkDoAchado",
  "conteudoFormatado",
  "featuredImage",
] as const;

const stripCdata = (value: string): string =>
  value
    .replace(/^<!\[CDATA\[/, "")
    .replace(/\]\]>$/, "")
    .trim();

const getChildText = (parent: Element, tagName: string): string => {
  const element = parent.getElementsByTagName(tagName)[0];
  return element?.textContent?.trim() ?? "";
};

const getTagContent = (xmlChunk: string, tagName: string): string => {
  const escapedTag = tagName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = xmlChunk.match(
    new RegExp(`<${escapedTag}(?:\\s[^>]*)?>([\\s\\S]*?)</${escapedTag}>`, "i"),
  );

  return match ? stripCdata(match[1]) : "";
};

const parseCustomFields = (item: Element): Partial<RssItem> => {
  const customFieldsElement = item.getElementsByTagName("customfields")[0];

  if (!customFieldsElement) {
    return {};
  }

  return {
    paisDeOrigem:
      getChildText(customFieldsElement, "paisDeOrigem") || undefined,
    cidadeDeOrigem:
      getChildText(customFieldsElement, "cidadeDeOrigem") || undefined,
    cidadeDeDestino:
      getChildText(customFieldsElement, "cidadeDeDestino") || undefined,
    aeroportoDeOrigem:
      getChildText(customFieldsElement, "aeroportoDeOrigem") || undefined,
    aeroportoDeDestino:
      getChildText(customFieldsElement, "aeroportoDeDestino") || undefined,
    paisDeDestino:
      getChildText(customFieldsElement, "paisDeDestino") || undefined,
    valorDaOferta:
      getChildText(customFieldsElement, "valorDaOferta") || undefined,
    valorMedio: getChildText(customFieldsElement, "valorMedio") || undefined,
    escala: getChildText(customFieldsElement, "escala") || undefined,
    tempoEstimado:
      getChildText(customFieldsElement, "tempoEstimado") || undefined,
    mesesDeViagem:
      getChildText(customFieldsElement, "mesesDeViagem") || undefined,
    descricaoDoAchado:
      getChildText(customFieldsElement, "descricaoDoAchado") || undefined,
    linkDoAchado:
      getChildText(customFieldsElement, "linkDoAchado") || undefined,
    conteudoFormatado:
      getChildText(customFieldsElement, "conteudoFormatado") || undefined,
    featuredImage:
      getChildText(customFieldsElement, "featuredImage") || undefined,
  };
};

const parseItemFromRegex = (itemXml: string): RssItem => {
  const customFieldsXml = getTagContent(itemXml, "customfields");
  const categories = Array.from(
    itemXml.matchAll(/<category(?:\s[^>]*)?>([\s\S]*?)<\/category>/gi),
  ).map((match) => stripCdata(match[1]));
  const guidMatch = itemXml.match(/<guid\b([^>]*)>([\s\S]*?)<\/guid>/i);

  const customFields = Object.fromEntries(
    CUSTOM_FIELD_TAGS.map((tag) => [
      tag,
      getTagContent(customFieldsXml, tag) || undefined,
    ]),
  ) as Partial<RssItem>;

  return {
    title: getTagContent(itemXml, "title"),
    link: getTagContent(itemXml, "link"),
    creator: getTagContent(itemXml, "dc:creator"),
    pubDate: getTagContent(itemXml, "pubDate"),
    categories,
    guid: {
      id: guidMatch ? stripCdata(guidMatch[2]) : "",
      isPermaLink: guidMatch
        ? !/isPermaLink=["']false["']/i.test(guidMatch[1])
        : true,
    },
    description: getTagContent(itemXml, "description"),
    postId: getTagContent(itemXml, "post-id"),
    ...customFields,
  };
};

const parseRssFeedWithRegex = (rssXml: string): RssItem[] =>
  Array.from(rssXml.matchAll(/<item\b[^>]*>([\s\S]*?)<\/item>/gi)).map(
    (match) => parseItemFromRegex(match[0]),
  );

export const parseRssFeed = async (rssXml: string): Promise<RssItem[]> => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(rssXml, "text/xml");
    const parserError = xmlDoc.getElementsByTagName("parsererror")[0];

    if (parserError) {
      return parseRssFeedWithRegex(rssXml);
    }

    const items = Array.from(xmlDoc.getElementsByTagName("item"));

    return items.map((item) => {
      const guidElement = item.getElementsByTagName("guid")[0];

      return {
        title: getChildText(item, "title"),
        link: getChildText(item, "link"),
        creator: getChildText(item, "dc:creator"),
        pubDate: getChildText(item, "pubDate"),
        categories: Array.from(item.getElementsByTagName("category")).map(
          (element) => element.textContent?.trim() ?? "",
        ),
        guid: {
          id: getChildText(item, "guid"),
          isPermaLink: guidElement?.getAttribute("isPermaLink") !== "false",
        },
        description: getChildText(item, "description"),
        postId: getChildText(item, "post-id"),
        ...parseCustomFields(item),
      };
    });
  } catch (error) {
    captureSentryException(error, {
      tags: {
        feature: "rss",
        operation: "parseRssFeed",
      },
    });
    console.error("[parseRssFeed] erro ao parsear:", error);
    throw error;
  }
};
